import {action, autorun, computed, makeObservable, observable} from 'mobx'
import User from '../models/User'
import {post} from "../common/requester";
import currentProduct from "../config/product";

class AppStore {
  jwt = '';
  user = new User();
  countries = []
  regions = []
  districts = []
  languages = []
  currencies = []
  tr = {}
  rand = 0
  sports = []
  roles = []
  product = currentProduct
  
  constructor() {
    makeObservable(this, {
      product: observable,
      jwt: observable,
      user: observable,
      countries: observable,
      regions: observable,
      districts: observable,
      languages: observable,
      currencies: observable,
      tr: observable,
      rand: observable,
      sports: observable,
      roles: observable,
      setJwt: action,
      init: action,
      initUser: action,
      setLanguages: action,
      setTranslations: action,
      setCountries: action,
      setRegions: action,
      setDistricts: action,
      fetchRegions: action,
      fetchDistricts: action,
      updateCountry: action,
      updateRegion: action,
      updateDistrict: action,
      userCountry: computed,
      setLanguage: action,
      t: action,
      fetchSports: action,
      setRoles: action,
      rolesLocalized: computed,
      getCurrencies: action,
      setCurrencies: action,
      getCountries: action,
      getRegions: action,
      getDistricts: action,
      getLocation: action,
      checkToken: action,
      productName: computed,
      entityName: computed
    });
  }

  setProperty(key, value) {
    this[key] = value
  }
  
  setJwt(token) {
    this.jwt = token
    sessionStorage.setItem('jwt', this.jwt)
  }

  async init(options = {}) {
    const {minimal = false} = options;

    const res = await post('init', {}).catch((err) => console.error('--init error', err))

    if (!res) {
        return;
    }

    this.initUser(res.user)
    this.setLanguages(res.languages)
    this.setTranslations(res.translations)
    this.setCountries(res.countries)
    this.setRoles(res.roles)
    this.setCurrencies(res.currencies)
    if (res.regions) {
      this.setRegions(res.regions.items)
    }
    if (res.districts) {
      this.setDistricts(res.districts.items)
    }

    if (!minimal) {
      await this.fetchSports()
    }
  }
  
  initUser(json) {
    this.user.fromJson(json)
    this.user.setProperty('initialized', true)
    if (this.user.id === 0) {
      const sessionLang = sessionStorage.getItem('language')
      if (sessionLang) {
        this.user.setProperty('language', sessionLang)
      }
    }
    sessionStorage.setItem('language', this.user.language)
  }

  setCountries(data) {
    sessionStorage.setItem('countries', JSON.stringify(data))
    this.countries = data
  }

  async fetchRegions() {
    if (this.user.id && this.user.country_id) {
      const r = await post('regions', {country_id: this.user.country_id, 'page_size': 200}).catch(()=>{})
      this.setRegions(r.items)
      if(!this.regions.some(r => r.id === this.user.region_id)) {
        this.user.setProperty('region_id', undefined)
        this.user.setProperty('district_id', undefined)
        this.setDistricts([])
      }
    }
  }

  setRegions(data) {
    sessionStorage.setItem('regions', JSON.stringify(data))
    this.regions = data
  }

  async fetchDistricts() {
    if (this.user.id && this.user.region_id) {
      const response = await post('districts', {region_id: this.user.region_id, 'page_size': 200}).catch(() => {})
      this.setDistricts(response.items)
      if(!this.districts.some(d => d.id === this.user.district_id)) {
        this.user.setProperty('district_id', undefined)
      }
    }
  }

  setDistricts(data) {
    sessionStorage.setItem('districts', JSON.stringify(data))
    this.districts = data
  }


  async updateCountry(country_id) {
    if (this.user.id) {
      await post('country', {'country_id': country_id}).catch((err)=>console.log(err))
      await this.fetchRegions()
    }
  }

  async updateRegion(region_id) {
    if (this.user.id && this.user.country_id) {
      await post('region', {'region_id': region_id}).catch((err)=>console.log(err))
      await this.fetchDistricts()
    }
  }

  async updateDistrict(district_id) {
    if (this.user.id && this.user.district_id) {
      await post('district', {'district_id': district_id}).catch((err)=>console.log(err))
    }
  }

  get
  userCountry() {
    return this.countries.find(c => c.id === this.user.country_id)
  }

  async setLanguage(language) {
    this.user.setProperty('language', language)
    sessionStorage.setItem('language', language)
    if (this.user.id) {
      await post('language', {'lng': language}).catch((err) => {console.log(err)})
    }
    const response = await post('translations', {language: language}).catch(err => console.log(err))
    this.setTranslations(response.t)
    const response2 = await post('countries').catch(err => console.log('--countries error', err))
    this.setCountries(response2.countries)
    if(this.user.id && this.user.country_id) {
      await this.fetchRegions()
    }
    if (this.user.id && this.user.region_id) {
      await this.fetchDistricts()
    }
  }
  
  setCurrencies(data) {
    this.currencies = data
  }

  setLanguages(data) {
    sessionStorage.setItem('languages', JSON.stringify(data))
    this.languages = data
  }

  get
  userLanguage() {
    return this.languages.find(l => l.id === this.user.language) || this.languages[0]
  }
  
  setTranslations(data) {
    sessionStorage.setItem('translations', JSON.stringify(data))
    this.setProperty('tr', data)
  }

  t(key, params = {}) {
    let str = this.tr[key] || key;

    const finalParams = {
      product: this.productName,
      entity: this.entityName,
      ...params,
    };

    Object.keys(finalParams).forEach((k) => {
      const value = finalParams[k] ?? "";
      str = str.replace(new RegExp(`{${k}}`, "g"), value);
    });

    return str;
  }
  
  async fetchSports() {
    if (this.sports.length === 0) {
      const response = await post('sports', {}).catch(() => {})
      this.setProperty('sports', response.sports)
    }
  }
  
  setRoles(data) {
    this.setProperty('roles', data)
  }
  
  get rolesLocalized() {
    return this.roles.map(r => ({id: r.id, name: r.data.names[this.user.language] || r.name}))
  }
  
  async getCurrencies() {
    const response = await post('currencies', {}).catch(() => {})
    this.setProperty('currencies', response.currencies)
  }
  
  async getCountries() {
    return post('countries', {})
  }
  
  async getRegions(country_id) {
    return post('regions', {country_id})
  }
  
  async getDistricts(region_id) {
    return post('districts', {region_id})
  }
  async getLocation() {
    return post('location', {})
  }
  
  async checkToken() {
    return post('check_token', {})
  }

  get productName() {
    return this.product.title
  }

  get entityName() {
    return this.product.product === 'club1' ? this.tr['club'] : this.tr['course']
  }
}
const appStore = new AppStore();
export default appStore;